/**
 * useDeepLinkListeners — Custom hook per centralizzare i deep-link
 * cross-tab dell'app (16/07/2026, estratto da App.js).
 *
 * OBIETTIVO
 * ---------
 * Ridurre la superficie di App.js e raggruppare in un unico posto
 * i 3 CustomEvent listener che permettono la navigazione contestuale
 * tra moduli diversi:
 *
 *   - `gi:open-quote`
 *       Da CaseDialog (pratica con sourceQuoteId), apri il preventivo
 *       di origine in Gestionale Pratiche → sub-tab Preventivi.
 *   - `gi:open-case-from-quote`
 *       Da PreventivoForm (preventivo convertito), apri la pratica
 *       generata in Gestionale Pratiche → sub-tab Registro Pratiche.
 *
 * NOTA: `gi:open-extra-deadline-from-diary` NON è gestito qui perché
 * necessita di setPendingDiaryDeadline (state locale a App.js) — resta
 * inline in App.js.
 *
 * OUTPUT
 * ------
 * `pendingEditQuoteId`: id del preventivo da aprire in edit-mode
 *   (consumato da <Modulistica initialEditFormId=...>).
 * `consumePendingEditQuoteId`: reset del flag, chiamato da Modulistica
 *   dopo aver caricato il form.
 */

import { useEffect, useState, useCallback } from "react";

export function useDeepLinkListeners({ cases, setActiveTab, setCasesSubTab, handleEditCase }) {
  const [pendingEditQuoteId, setPendingEditQuoteId] = useState(null);
  const consumePendingEditQuoteId = useCallback(() => setPendingEditQuoteId(null), []);

  // Deep-link "gi:open-quote" da CaseDialog → preventivo di origine.
  useEffect(() => {
    const onOpenQuote = (ev) => {
      const qid = ev?.detail?.quoteId;
      if (!qid) return;
      setCasesSubTab("preventivi");
      setPendingEditQuoteId(qid);
      setActiveTab("cases");
    };
    window.addEventListener("gi:open-quote", onOpenQuote);
    return () => window.removeEventListener("gi:open-quote", onOpenQuote);
  }, [setActiveTab, setCasesSubTab]);

  // Deep-link "gi:open-case-from-quote" da PreventivoForm.
  useEffect(() => {
    const onOpenCaseFromQuote = (ev) => {
      const cid = ev?.detail?.caseId;
      if (!cid) return;
      const target = (cases || []).find((c) => c.id === cid);
      if (target) {
        setCasesSubTab("registro");
        setActiveTab("cases");
        handleEditCase(target);
      }
    };
    window.addEventListener("gi:open-case-from-quote", onOpenCaseFromQuote);
    return () => window.removeEventListener("gi:open-case-from-quote", onOpenCaseFromQuote);
  }, [cases, handleEditCase, setActiveTab, setCasesSubTab]);

  return { pendingEditQuoteId, consumePendingEditQuoteId };
}

export default useDeepLinkListeners;
